const FR_LOCALE = "fr-FR"

/**
 * Format an ISO date string to a localized date.
 * Example: "2026-03-26T10:00:00+00:00" → "26 mars 2026"
 */
export function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString(FR_LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

/**
 * Format an ISO date string to a localized time.
 * Example: "2026-03-26T10:00:00+00:00" → "11:00" (in CET)
 */
export function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString(FR_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
  })
}

/**
 * Format an ISO date string to a localized date and time.
 * Example: "2026-03-26T10:00:00+00:00" → "26 mars 2026 à 11:00"
 */
export function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString(FR_LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/**
 * Format a short date: "26/03/2026"
 */
export function formatShortDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString(FR_LOCALE)
}

/**
 * Format a relative time: "dans 2 heures", "il y a 3 jours"
 */
export function formatRelative(isoString: string): string {
  const now = new Date()
  const date = new Date(isoString)
  const diffMs = date.getTime() - now.getTime()
  const diffSeconds = Math.round(diffMs / 1000)
  const diffMinutes = Math.round(diffSeconds / 60)
  const diffHours = Math.round(diffMinutes / 60)
  const diffDays = Math.round(diffHours / 24)

  const rtf = new Intl.RelativeTimeFormat(FR_LOCALE, { numeric: "auto" })

  if (Math.abs(diffDays) >= 1) return rtf.format(diffDays, "day")
  if (Math.abs(diffHours) >= 1) return rtf.format(diffHours, "hour")
  if (Math.abs(diffMinutes) >= 1) return rtf.format(diffMinutes, "minute")
  return rtf.format(diffSeconds, "second")
}

/**
 * Convert JavaScript weekday (0=Sunday) to ISO 8601 (1=Monday, 7=Sunday).
 */
export function jsWeekdayToIso(jsDay: number): number {
  return jsDay === 0 ? 7 : jsDay
}

/**
 * Convert ISO 8601 weekday (1=Monday, 7=Sunday) to JavaScript (0=Sunday).
 */
export function isoWeekdayToJs(isoDay: number): number {
  return isoDay === 7 ? 0 : isoDay
}

/**
 * Get the French name of a weekday from ISO number.
 */
const WEEKDAY_NAMES: Record<number, string> = {
  1: "Lundi",
  2: "Mardi",
  3: "Mercredi",
  4: "Jeudi",
  5: "Vendredi",
  6: "Samedi",
  7: "Dimanche",
}

export function getWeekdayName(isoDay: number): string {
  return WEEKDAY_NAMES[isoDay] ?? ""
}

/**
 * Format a duration in minutes to a readable string.
 *
 * Examples:
 *  - 30           → "30min"
 *  - 60           → "1h"
 *  - 90           → "1h30"
 *  - 247          → "4h07"
 *  - 60 * 24      → "1j"
 *  - 60 * 24 + 90 → "1j 1h30"
 *
 * Au-delà du jour, les minutes ne sont affichées que si elles sont
 * non nulles, pour éviter du bruit ("1j 0h30" → "1j 0h30" reste utile,
 * "2j 0h00" → "2j").
 */
export function formatDuration(totalMinutes: number): string {
  const minutes = Math.max(0, Math.round(totalMinutes))

  const days = Math.floor(minutes / (60 * 24))
  const hours = Math.floor((minutes % (60 * 24)) / 60)
  const mins = minutes % 60

  if (days > 0) {
    if (hours === 0 && mins === 0) return `${days}j`
    if (mins === 0) return `${days}j ${hours}h`
    return `${days}j ${hours}h${String(mins).padStart(2, "0")}`
  }

  if (hours === 0) return `${mins}min`
  if (mins === 0) return `${hours}h`
  return `${hours}h${String(mins).padStart(2, "0")}`
}

/**
 * Check if two dates fall on the same calendar day.
 */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

